<script setup>
import { computed, reactive, ref, watch } from 'vue'
import CropEditor from '../../components/CropEditor.vue'

const props = defineProps({ assets: { type: Array, default: () => [] } })

// ---------------------------------------------------------------------------
// Option state. Defaults are the imgproxy "neutral" values, so buildParams()
// can omit anything left untouched and keep the generated URLs short.
// ---------------------------------------------------------------------------
const DEFAULTS = {
  id: props.assets[0]?.id ?? null,
  // resize
  w: 800, h: null, rt: 'fit',
  g: 'ce', gx: 0, gy: 0,
  el: false, ex: false, exg: 'ce',
  dpr: 1, z: 1,
  // crop — cg 'fp' + cfx/cfy drive the interactive frame; cropOn gates it
  cw: null, ch: null, cg: 'ce', cfx: 0.5, cfy: 0.5, cropOn: false,
  // filters
  bl: 0, sh: 0, pix: 0,
  // transform
  rot: '0', flh: false, flv: false,
  pt: 0, pr: 0, pb: 0, pl: 0,
  tt: 0, tc: '#ffffff', teh: false, tev: false,
  // color (free)
  bgOn: false, bg: '#ffffff',
  // output
  f: 'webp', q: 80, sm: false, scp: false,
}
const o = reactive({ ...DEFAULTS })
function reset() {
  const id = o.id
  Object.assign(o, DEFAULTS)
  o.id = id
}

const selected = computed(() => props.assets.find((a) => a.id === o.id))
const isVideo = computed(() => selected.value?.kind === 'video')

const assetItems = computed(() =>
  props.assets.map((a) => ({
    label: `ID ${a.id} — ${a.filename}${a.kind === 'video' ? ' · video' : ''}`,
    value: a.id,
  })),
)

// ---------------------------------------------------------------------------
// Select option lists
// ---------------------------------------------------------------------------
const resizeItems = [
  { label: 'fit — shrink to fit inside w×h', value: 'fit' },
  { label: 'fill — crop to fill w×h exactly', value: 'fill' },
  { label: 'fill-down — like fill, never upscale', value: 'fill-down' },
  { label: 'force — stretch to w×h', value: 'force' },
  { label: 'auto — fill/fit by orientation', value: 'auto' },
]
const gravityItems = [
  { label: 'Center', value: 'ce' },
  { label: 'Smart (detect subject)', value: 'sm' },
  { label: 'North', value: 'no' },
  { label: 'South', value: 'so' },
  { label: 'East', value: 'ea' },
  { label: 'West', value: 'we' },
  { label: 'North-East', value: 'noea' },
  { label: 'North-West', value: 'nowe' },
  { label: 'South-East', value: 'soea' },
  { label: 'South-West', value: 'sowe' },
]
// Extend positioning has no "smart" — it just places the (smaller) image on the canvas.
const gravityNoSmart = gravityItems.filter((g) => g.value !== 'sm')
const formatItems = ['webp', 'avif', 'jpg', 'png', 'gif']
const rotateItems = [
  { label: '0°', value: '0' },
  { label: '90°', value: '90' },
  { label: '180°', value: '180' },
  { label: '270°', value: '270' },
]

const tabItems = [
  { label: 'Resize', icon: 'i-lucide-scaling', value: 'resize' },
  { label: 'Crop', icon: 'i-lucide-crop', value: 'crop' },
  { label: 'Filters', icon: 'i-lucide-aperture', value: 'filters' },
  { label: 'Transform', icon: 'i-lucide-flip-horizontal-2', value: 'transform' },
  { label: 'Adjust', icon: 'i-lucide-sliders-horizontal', value: 'adjust' },
  { label: 'Output', icon: 'i-lucide-file-image', value: 'output' },
]
const tab = ref('resize')

// ---------------------------------------------------------------------------
// Interactive crop. The frame lives on the Crop tab and edits cw/ch (source px)
// + cfx/cfy (focus point). cropOn gates whether a crop is applied at all.
// ---------------------------------------------------------------------------
// Checkerboard backing so image transparency is visible in previews.
const checkerStyle = {
  backgroundImage:
    'linear-gradient(45deg,var(--ui-bg-elevated) 25%,transparent 25%),linear-gradient(-45deg,var(--ui-bg-elevated) 25%,transparent 25%),linear-gradient(45deg,transparent 75%,var(--ui-bg-elevated) 75%),linear-gradient(-45deg,transparent 75%,var(--ui-bg-elevated) 75%)',
  backgroundSize: '20px 20px',
  backgroundPosition: '0 0,0 10px,10px -10px,-10px 0',
}

const dims = computed(() =>
  selected.value?.width && selected.value?.height
    ? { w: selected.value.width, h: selected.value.height }
    : null,
)
const cropMode = computed(() => tab.value === 'crop' && o.cropOn && !!dims.value)

// The ghost = the original, fitted (no crop), served through the same signed pipeline.
const ghostUrl = computed(() => {
  if (!o.id || !dims.value) return ''
  const w = Math.min(dims.value.w, 1200)
  return `${window.location.origin}/assets/${o.id}/playground_img?rt=fit&w=${w}&f=webp`
})

// Two-way binding for CropEditor. Writing marks the crop as focus-point based.
const cropModel = computed({
  get: () => ({ cw: o.cw, ch: o.ch, cfx: o.cfx, cfy: o.cfy }),
  set: (v) => {
    o.cw = v.cw
    o.ch = v.ch
    o.cfx = v.cfx
    o.cfy = v.cfy
    o.cg = 'fp'
  },
})

function initCrop() {
  if (!dims.value) return
  o.cw = Math.round(dims.value.w * 0.6)
  o.ch = Math.round(dims.value.h * 0.6)
  o.cfx = 0.5
  o.cfy = 0.5
  o.cg = 'fp'
}

// Aspect-ratio presets for the frame (UI-only; the frame reshapes on change).
const aspectPresets = [
  { label: 'Free', value: null },
  { label: '1:1', value: 1 },
  { label: '16:9', value: 16 / 9 },
  { label: '4:3', value: 4 / 3 },
  { label: 'Original', value: 'orig' },
]
const cropAspectKey = ref('Free')
const cropAspect = ref(null)
function setAspect(preset) {
  cropAspectKey.value = preset.label
  // A fresh object each time so CropEditor's aspect watcher always fires.
  cropAspect.value =
    preset.value === 'orig' ? (dims.value ? dims.value.w / dims.value.h : null) : preset.value
}

// Entering the Crop tab enables + seeds a crop; switching asset resets it.
watch(tab, (t) => {
  if (t === 'crop' && dims.value && !o.cw) {
    o.cropOn = true
    initCrop()
  }
})
watch(
  () => o.id,
  () => {
    o.cw = null
    o.ch = null
    o.cropOn = false
    cropAspectKey.value = 'Free'
    cropAspect.value = null
    if (tab.value === 'crop' && dims.value) {
      o.cropOn = true
      initCrop()
    }
  },
)

// ---------------------------------------------------------------------------
// URL building. Only non-default values are emitted (matches the server, which
// treats absent params as "no option"). The query is validated + clamped again
// server-side by ImgproxyOptions, so this is purely cosmetic minimization.
// ---------------------------------------------------------------------------
function buildQuery() {
  const p = new URLSearchParams()
  const set = (k, v) => p.set(k, v)

  if (o.w) set('w', o.w)
  if (o.h) set('h', o.h)
  if (o.rt !== 'fit') set('rt', o.rt)
  if (o.g !== 'ce' || o.gx || o.gy) {
    set('g', o.g)
    if (o.g !== 'sm') {
      if (o.gx) set('gx', o.gx)
      if (o.gy) set('gy', o.gy)
    }
  }
  if (o.el) set('el', '1')
  if (o.ex) {
    set('ex', '1')
    if (o.exg !== 'ce') set('exg', o.exg)
  }
  if (o.dpr > 1) set('dpr', o.dpr)
  if (o.z != 1) set('z', o.z)

  if (o.cropOn && o.cw && o.ch) {
    set('cw', o.cw)
    set('ch', o.ch)
    set('cg', 'fp')
    set('cfx', o.cfx)
    set('cfy', o.cfy)
  }

  if (o.bl > 0) set('bl', o.bl)
  if (o.sh > 0) set('sh', o.sh)
  if (o.pix > 0) set('pix', o.pix)

  if (o.rot !== '0') set('rot', o.rot)
  if (o.flh) set('flh', '1')
  if (o.flv) set('flv', '1')
  if (o.pt) set('pt', o.pt)
  if (o.pr) set('pr', o.pr)
  if (o.pb) set('pb', o.pb)
  if (o.pl) set('pl', o.pl)
  if (o.tt > 0) {
    set('tt', o.tt)
    if (o.tc) set('tc', o.tc)
    if (o.teh) set('teh', '1')
    if (o.tev) set('tev', '1')
  }

  if (o.bgOn && o.bg) set('bg', o.bg)

  if (o.f !== 'webp') set('f', o.f)
  if (o.q !== 80) set('q', o.q)
  if (o.sm) set('sm', '1')
  if (o.scp) set('scp', '1')

  return p.toString()
}

// ---------------------------------------------------------------------------
// Live preview. Debounced fetch of the JSON endpoint returns the raw signed
// imgproxy URL (what the <img> loads + what we display) and the stable share
// link. Both come from the same server-validated options, so they never drift.
// ---------------------------------------------------------------------------
const previewSrc = ref('')
const rawUrl = ref('')
const shareUrl = ref('')
const previewError = ref(false)
const loading = ref(false)
let timer = null
let reqId = 0

function refresh() {
  if (!o.id) {
    previewSrc.value = rawUrl.value = shareUrl.value = ''
    return
  }
  clearTimeout(timer)
  loading.value = true
  timer = setTimeout(async () => {
    const mine = ++reqId
    try {
      const res = await fetch(`/assets/${o.id}/imgproxy_url?${buildQuery()}`, {
        headers: { Accept: 'application/json' },
      })
      if (mine !== reqId) return // a newer request superseded this one
      const data = await res.json()
      rawUrl.value = data.imgproxy_url
      shareUrl.value = data.share_url
      previewError.value = false
      previewSrc.value = data.imgproxy_url
    } catch {
      if (mine === reqId) previewError.value = true
    } finally {
      if (mine === reqId) loading.value = false
    }
  }, 250)
}

// Any option change re-derives the preview + URLs.
watch(o, refresh, { immediate: true, deep: true })

// ---------------------------------------------------------------------------
// Copy-to-clipboard (one flag per field so buttons toggle independently)
// ---------------------------------------------------------------------------
const copied = reactive({ share: false, raw: false })
async function copy(text, key) {
  try {
    await navigator.clipboard.writeText(text)
    copied[key] = true
    setTimeout(() => (copied[key] = false), 1500)
  } catch {
    // clipboard API can be unavailable (permissions) — the field stays selectable
  }
}
</script>

<template>
  <UCard>
    <template #header>
      <div class="flex items-start justify-between gap-4">
        <div>
          <h2 class="font-semibold">imgproxy Playground</h2>
          <p class="text-sm text-muted">
            Experiment with every free imgproxy transformation — the preview and URL update live.
          </p>
        </div>
        <UButton
          icon="i-lucide-rotate-ccw"
          color="neutral"
          variant="ghost"
          label="Reset"
          @click="reset"
        />
      </div>
    </template>

    <div v-if="assets.length === 0" class="text-muted">
      No assets yet —
      <UButton to="/assets/new" variant="link" label="upload an image first" />
    </div>

    <div v-else class="space-y-6">
      <!-- Controls: pick the asset, then tune it -->
      <UFormField label="Asset">
        <USelect v-model="o.id" :items="assetItems" class="w-full" />
      </UFormField>

      <p v-if="isVideo" class="text-xs text-muted">
        <UIcon name="i-lucide-info" class="align-text-bottom" />
        For a video, imgproxy transforms its poster frame.
      </p>

      <div class="space-y-5">
        <UTabs v-model="tab" :items="tabItems" size="sm" />

        <!-- ---- Resize ---- -->
        <div v-show="tab === 'resize'" class="space-y-4">
          <div class="grid grid-cols-2 gap-4">
            <UFormField label="Width (px)" hint="0 = auto from height">
              <UInput v-model.number="o.w" type="number" min="0" max="4000" class="w-full" />
            </UFormField>
            <UFormField label="Height (px)" hint="blank = auto from width">
              <UInput v-model.number="o.h" type="number" min="0" max="4000" placeholder="auto" class="w-full" />
            </UFormField>
          </div>
          <div class="grid grid-cols-2 gap-4">
            <UFormField label="Resizing type">
              <USelect v-model="o.rt" :items="resizeItems" class="w-full" />
            </UFormField>
            <UFormField label="Gravity" hint="anchor when cropping">
              <USelect v-model="o.g" :items="gravityItems" class="w-full" />
            </UFormField>
          </div>
          <div v-if="!['ce', 'sm'].includes(o.g)" class="grid grid-cols-2 gap-4">
            <UFormField label="Gravity X offset">
              <UInput v-model.number="o.gx" type="number" class="w-full" />
            </UFormField>
            <UFormField label="Gravity Y offset">
              <UInput v-model.number="o.gy" type="number" class="w-full" />
            </UFormField>
          </div>
          <div class="flex flex-wrap items-center gap-6">
            <USwitch v-model="o.el" label="Enlarge" description="upscale smaller images" />
            <USwitch v-model="o.ex" label="Extend" description="pad up to w×h" />
          </div>
          <UFormField v-if="o.ex" label="Extend position">
            <USelect v-model="o.exg" :items="gravityNoSmart" class="w-full" />
          </UFormField>
          <div class="grid grid-cols-2 gap-4">
            <UFormField :label="`DPR — ${o.dpr}×`" hint="device pixel ratio">
              <USlider v-model="o.dpr" :min="1" :max="4" :step="0.5" />
            </UFormField>
            <UFormField :label="`Zoom — ${o.z}×`">
              <USlider v-model="o.z" :min="0.5" :max="4" :step="0.1" />
            </UFormField>
          </div>
        </div>

        <!-- ---- Crop ---- -->
        <div v-show="tab === 'crop'" class="space-y-4">
          <p v-if="!dims" class="text-sm text-muted">
            This asset has no known dimensions yet, so the interactive crop isn't available.
          </p>
          <template v-else>
            <div class="flex items-center justify-between gap-3">
              <USwitch v-model="o.cropOn" label="Apply crop" description="drag the frame on the image below" />
              <UButton
                v-if="o.cropOn"
                icon="i-lucide-maximize"
                color="neutral"
                variant="ghost"
                size="xs"
                label="Recenter"
                @click="initCrop"
              />
            </div>

            <template v-if="o.cropOn">
              <UFormField label="Aspect ratio">
                <div class="flex flex-wrap gap-1.5">
                  <UButton
                    v-for="p in aspectPresets"
                    :key="p.label"
                    :label="p.label"
                    size="xs"
                    :color="cropAspectKey === p.label ? 'primary' : 'neutral'"
                    :variant="cropAspectKey === p.label ? 'solid' : 'outline'"
                    @click="setAspect(p)"
                  />
                </div>
              </UFormField>

              <div class="grid grid-cols-2 gap-4">
                <UFormField label="Crop width (px)">
                  <UInput v-model.number="o.cw" type="number" min="1" max="4000" class="w-full" />
                </UFormField>
                <UFormField label="Crop height (px)">
                  <UInput v-model.number="o.ch" type="number" min="1" max="4000" class="w-full" />
                </UFormField>
              </div>
              <p class="text-xs text-muted">
                <UIcon name="i-lucide-move" class="align-text-bottom" />
                Drag the frame to move it (focus point), or pull a corner to resize.
              </p>
            </template>
          </template>
        </div>

        <!-- ---- Filters ---- -->
        <div v-show="tab === 'filters'" class="space-y-4">
          <UFormField :label="`Blur — ${o.bl}`" hint="Gaussian sigma">
            <USlider v-model="o.bl" :min="0" :max="30" :step="0.5" />
          </UFormField>
          <UFormField :label="`Sharpen — ${o.sh}`">
            <USlider v-model="o.sh" :min="0" :max="5" :step="0.1" />
          </UFormField>
          <UFormField :label="`Pixelate — ${o.pix}px`">
            <USlider v-model="o.pix" :min="0" :max="100" :step="1" />
          </UFormField>
        </div>

        <!-- ---- Transform ---- -->
        <div v-show="tab === 'transform'" class="space-y-4">
          <div class="grid grid-cols-2 gap-4">
            <UFormField label="Rotate">
              <USelect v-model="o.rot" :items="rotateItems" class="w-full" />
            </UFormField>
            <UFormField label="Flip">
              <div class="flex items-center gap-4 pt-1.5">
                <USwitch v-model="o.flh" label="H" />
                <USwitch v-model="o.flv" label="V" />
              </div>
            </UFormField>
          </div>
          <UFormField label="Padding (px)" hint="top · right · bottom · left">
            <div class="grid grid-cols-4 gap-2">
              <UInput v-model.number="o.pt" type="number" min="0" placeholder="T" />
              <UInput v-model.number="o.pr" type="number" min="0" placeholder="R" />
              <UInput v-model.number="o.pb" type="number" min="0" placeholder="B" />
              <UInput v-model.number="o.pl" type="number" min="0" placeholder="L" />
            </div>
          </UFormField>
          <UFormField :label="`Trim — threshold ${o.tt}`" hint="0 = off; removes matching border">
            <USlider v-model="o.tt" :min="0" :max="100" :step="1" />
          </UFormField>
          <div v-if="o.tt > 0" class="flex flex-wrap items-center gap-4">
            <UFormField label="Trim color">
              <input v-model="o.tc" type="color" class="h-9 w-16 cursor-pointer rounded border border-default bg-default" />
            </UFormField>
            <USwitch v-model="o.teh" label="Equal horizontal" />
            <USwitch v-model="o.tev" label="Equal vertical" />
          </div>
        </div>

        <!-- ---- Adjust ---- -->
        <div v-show="tab === 'adjust'" class="space-y-4">
          <UFormField label="Background">
            <div class="flex items-center gap-3">
              <USwitch v-model="o.bgOn" label="Set background" />
              <input
                v-model="o.bg"
                type="color"
                :disabled="!o.bgOn"
                class="h-9 w-16 cursor-pointer rounded border border-default bg-default disabled:opacity-40"
              />
              <span class="font-mono text-xs text-muted">{{ o.bgOn ? o.bg : 'none' }}</span>
            </div>
          </UFormField>

          <div class="rounded-lg border border-dashed border-default p-4">
            <div class="mb-3 flex items-center gap-2">
              <p class="text-sm font-medium">Color adjustments</p>
              <UBadge color="warning" variant="subtle" size="sm" label="Pro" />
              <UButton
                to="https://docs.imgproxy.net/usage/processing#adjust"
                target="_blank"
                variant="link"
                size="xs"
                trailing-icon="i-lucide-external-link"
                label="docs"
              />
            </div>
            <p class="mb-4 text-xs text-muted">
              Brightness, contrast &amp; saturation require imgproxy Pro — our server is the
              open-source build, so these are disabled here.
            </p>
            <div class="space-y-3 opacity-60">
              <UFormField label="Brightness">
                <USlider :model-value="0" :min="-100" :max="100" disabled />
              </UFormField>
              <UFormField label="Contrast">
                <USlider :model-value="1" :min="0" :max="2" :step="0.1" disabled />
              </UFormField>
              <UFormField label="Saturation">
                <USlider :model-value="1" :min="0" :max="2" :step="0.1" disabled />
              </UFormField>
            </div>
          </div>
        </div>

        <!-- ---- Output ---- -->
        <div v-show="tab === 'output'" class="space-y-4">
          <div class="grid grid-cols-2 gap-4">
            <UFormField label="Format">
              <USelect v-model="o.f" :items="formatItems" class="w-full" />
            </UFormField>
            <UFormField :label="`Quality — ${o.q}`" hint="lossy formats only">
              <USlider v-model="o.q" :min="1" :max="100" :step="1" />
            </UFormField>
          </div>
          <div class="flex flex-wrap items-center gap-6">
            <USwitch v-model="o.sm" label="Strip metadata" description="remove EXIF/IPTC" />
            <USwitch v-model="o.scp" label="Strip color profile" />
          </div>
        </div>
      </div>

      <!-- ============================ Preview + URLs ========================= -->
      <!-- Crop mode: interactive ghost + frame, plus the live cropped result. -->
      <div v-if="cropMode" class="space-y-3">
        <div class="rounded-lg border border-default p-3">
          <CropEditor
            v-model="cropModel"
            :src="ghostUrl"
            :natural-width="dims.w"
            :natural-height="dims.h"
            :aspect="cropAspect"
          />
          <p class="mt-2 text-center text-xs text-muted">
            Original {{ dims.w }}×{{ dims.h }} — the bright frame is what gets cut
          </p>
        </div>

        <UFormField label="Result" hint="cropped output, rendered by imgproxy">
          <div
            class="relative flex min-h-32 items-center justify-center overflow-hidden rounded-lg border border-default p-2"
            :style="checkerStyle"
          >
            <img
              v-if="previewSrc && !previewError"
              :src="previewSrc"
              alt="imgproxy preview"
              class="max-h-60 max-w-full rounded"
            />
            <p v-else-if="previewError" class="text-sm text-error">
              imgproxy couldn't render this combination.
            </p>
            <p v-else class="text-sm text-muted">Loading…</p>
          </div>
        </UFormField>
      </div>

      <!-- Normal preview -->
      <div
        v-else
        class="relative flex min-h-64 items-center justify-center overflow-hidden rounded-lg border border-default p-2"
        :style="checkerStyle"
      >
        <img
          v-if="previewSrc && !previewError"
          :src="previewSrc"
          alt="imgproxy preview"
          class="max-h-[32rem] max-w-full rounded"
        />
        <p v-else-if="previewError" class="text-sm text-error">
          imgproxy couldn't render this combination.
        </p>
        <p v-else class="text-sm text-muted">Loading…</p>
        <span
          v-if="loading && previewSrc"
          class="absolute right-2 top-2 rounded bg-default/80 px-2 py-0.5 text-xs text-muted"
        >
          updating…
        </span>
      </div>

      <UFormField label="Shareable URL" hint="stable link — signs a fresh imgproxy URL server-side">
        <div class="flex gap-2">
          <UInput :model-value="shareUrl" readonly class="grow font-mono" :ui="{ base: 'text-xs' }" />
          <UButton
            :icon="copied.share ? 'i-lucide-check' : 'i-lucide-clipboard'"
            :color="copied.share ? 'success' : 'primary'"
            @click="copy(shareUrl, 'share')"
          />
        </div>
      </UFormField>

      <UFormField label="Raw imgproxy URL" hint="signed URL the image is served from">
        <div class="flex gap-2">
          <UInput :model-value="rawUrl" readonly class="grow font-mono" :ui="{ base: 'text-xs' }" />
          <UButton
            :icon="copied.raw ? 'i-lucide-check' : 'i-lucide-clipboard'"
            :color="copied.raw ? 'success' : 'neutral'"
            variant="subtle"
            @click="copy(rawUrl, 'raw')"
          />
        </div>
      </UFormField>
    </div>

    <template #footer>
      <p class="text-xs text-muted">
        The
        <UButton
          v-if="selected"
          :to="`/assets/${o.id}/file`"
          target="_blank"
          variant="link"
          size="xs"
          class="px-0"
          :label="selected.filename"
        />
        original is served from Active Storage; imgproxy derives every variant on the fly.
      </p>
    </template>
  </UCard>
</template>
